import React, { useCallback, useRef, useEffect, useState } from 'react'
import { formStyle, stepNumberStyle, formTitleStyle, formDesStyle, topUnderlineStyle, sectionContainer, smallLabel, pdapNotice, typographyStyle, typographySpanStyle } from '../../../constant/material_style';
import { Box, Grid, Typography } from '@mui/material';
import CustomTextfield from '../../../component/custom_textfield';
import { useForm } from 'react-hook-form';
import _ from 'lodash';
import { pdpa_co_type } from '../../../constant/common_use';
import { useStateContext } from '../../../contexts/StepFormContextProvider';
import { handleDeleteAttachment, handleUpdateForm, uploadImageToServer } from '../../../api/rc_admission_form';
import { b64toBlob, base64ToImage, uploadImgToServer } from '../../../utils/helper/rc_form_helper';

const Pdpa = ({ currentStep, setCurrentStep, formValues, setFormValues, getSignature, handleUpdateSignature }) => {
	const { control, reset, handleSubmit, setValue, formState: { errors }} = useForm({ mode: 'onChange' });
	const { formId } = useStateContext();

	const [savedSignature, setSavedSignature] = useState(false);

	useEffect(() => {
		reset(formValues);
	}, [formValues.co_name, formValues.co_ic_passport]);

	useEffect(() => {
		setValue('pdpa_co_date', getSignature(pdpa_co_type)?.date);
	}, [setValue, formValues]);

	const coRef = useRef({});

	const debounceSetValues = useCallback(
		_.debounce((name, value, type) => {
			setValue(name, value, { shouldValidate: true });

			setFormValues((prev) => {
				const newFormValues = { ...prev, [name]: value }
				const updatedValue = value.filter(val => val.type === type);

				if(formId) {
					handleUpdateForm(formId, {signatures: updatedValue});
				}

				setFormValues(newFormValues);
				return newFormValues;
			});
		}, 800), [setValue, setFormValues, formId]
	);

	const clearSignature = async (e, signatureRef, type) => {
		e.preventDefault();
		signatureRef.current.clear();

		const filteredData = formValues.signatures.filter((signature) => signature.type === type);

		if (filteredData) {
			const updatedSignatures = formValues.signatures.map((signature) => {
				if(signature.type === type) {
					if(signature.hasOwnProperty('id')) {
						delete signature.id;
					}

					signature.signature_url_s3 = '';
				}

				return signature;
			});

			if (filteredData[0].signature_url_s3.trim() !== '') {
				const deleteSig = {...filteredData[0]};
				delete deleteSig.ic_passport;
				delete deleteSig.name;
				delete deleteSig.type;
				deleteSig.delete = 1;

				await handleDeleteAttachment(formId, { signatures: [deleteSig] });
			}

			setFormValues({ ...formValues, signatures: updatedSignatures });
			await handleUpdateForm(formId, { signatures: filteredData });
		}
	};

	const saveSignature = async (e, canva, type, name, ic_passport) => {
		e.preventDefault();
		const url = canva.current.getTrimmedCanvas().toDataURL('image/png');
		const base64Data = url.replace(/^data:image\/\w+;base64,/, '');

		const imgFile = base64ToImage(url, type);
		const imgBlob = b64toBlob(base64Data, 'image/png');

		const signatureAttachments = [];
		let imgObj = {
			prefix: "1/admission/documents",
			filename: imgFile.name,
		};
		signatureAttachments.push(imgObj);
		let transformData = { payload: signatureAttachments }
		const res = await uploadImageToServer(transformData);

		const convertedAttachments = [];
		if(res) {
			const uploadUrl = Object.keys(res).map(key => {
				const attachmentArr = res[key];
				return attachmentArr[0].uploadUrl;
			}).pop();

			await uploadImgToServer(uploadUrl, imgBlob);

			Object.keys(res).forEach(key => {
				const attachmentArr = res[key];

				attachmentArr.forEach(attachment => {
					const convertedFiles = {
						attachment_url_s3: attachment?.downloadUrl,
						attachment_name: attachment?.filePath.split('/').pop(),
					}

					convertedAttachments.push(convertedFiles);
				});
			})
		}

		const result = handleUpdateSignature(type, 'signature_url_s3', convertedAttachments[0].attachment_url_s3, name, ic_passport);
		if(result) {
			setSavedSignature(result);
		}
	};

	const handleChange = (type, objKey, val) => {
		const updatedSignatures = formValues.signatures.map((signature) => {
			return signature.type === type ? {
				...signature, [objKey]: val,
			} : signature
		});

		debounceSetValues('signatures', updatedSignatures, type);
	}

	const goback = (e) => {
		setCurrentStep(currentStep - 1);
	}

	const onSubmit = (e) => {
		setCurrentStep(currentStep + 1);
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<Box style={formStyle}>
				<Grid container alignItems="flex-start" spacing={2} style={{ marginBottom: '20px', overflowY: 'auto' }}>
					<Grid item>
						<Box style={stepNumberStyle}>{currentStep}</Box>
					</Grid>
					<Grid item xs style={{ paddingLeft: '0' }}>
						<Typography style={formTitleStyle}>PDPA</Typography>
						<Typography style={formDesStyle}>Personal Data Protection Act</Typography>
					</Grid>
				</Grid>

				<>
					<Box style={sectionContainer}>
						<Box style={topUnderlineStyle}>
							<Typography style={formDesStyle}>Resident</Typography>
						</Box>
					</Box>

					<Box style={sectionContainer}>
						<Typography style={pdapNotice}>
							NOTICE UNDER THE PERSONAL DATA PROTECTION ACT 2010
						</Typography>
						<Typography style={pdapNotice}>
							NOTIS DI BAWAH AKTA PERLINDUNGAN DATA PERIBADI 2010
						</Typography>
					</Box>
					<div style={{ marginBottom: '20px' }}></div>

					<Box style={sectionContainer}>
					<Typography style={smallLabel}>
						The Personal Data Protection Act 2010 (the “Act”), which regulates the processing of personal data in
						commercial transactions, applies to Komune Care Centre Sdn Bhd (Company No.: -202101014451) (“our”, “us”
						or “we”). For the purpose of this written notice (“Notice”), the terms “personal data” and “processing” shall
						have the same meaning as prescribed in the Act.
					</Typography>
				</Box>

				<Box style={sectionContainer}>
					<Typography style={smallLabel}>
						Akta Perlindungan Data Peribadi 2010 (“Akta” tersebut) yang mengawal selia pemprosesan data peribadi dalam
						transaksi komersial, terpakai kepada Komune Care Centre Sdn Bhd (Company No.: -202101014451 ) (“kami”).
						Untuk tujuan notis bertulis ini (“Notis”), terma-terma “data peribadi” dan “pemprosesan” mempunyai maksud
						yang sama seperti yang ditakrif dalam Akta tersebut.
					</Typography>
				</Box>

				<Box style={sectionContainer}>
					<Typography style={typographyStyle}>
						<span style={typographySpanStyle}>1.</span>
						This Notice serves to inform you that your personal data will be and/or have been processed by us or
						on our behalf and you hereby give your consent to the processing of your personal data.
						<br /><br />
						Notis ini bertujuan untuk memaklumkan kepada anda bahawa data peribadi anda akan dan/atau telah diproses
						oleh atau bagi pihak kami dan anda dengan ini memberikan persetujuan anda bagi pemprosesan data peribadi
						anda.
					</Typography>
					<Typography style={typographyStyle}>
						<span style={typographySpanStyle}>2.</span>
						The Personal Data Protection Act 2010 (the “Act”), which regulates the processing of personal data in
						commercial transactions, applies to Komune Care Centre Sdn Bhd (Company No.: -202101014451) (“our”, “us”
						or “we”). For the purpose of this written notice (“Notice”), the terms “personal data” and “processing” shall
						have the same meaning as prescribed in the Act.
					</Typography>

					<Typography style={typographyStyle}>
						<span style={typographySpanStyle}>3.</span>
						Akta Perlindungan Data Peribadi 2010 (“Akta” tersebut) yang mengawal selia pemprosesan data peribadi dalam
						transaksi komersial, terpakai kepada Komune Care Centre Sdn Bhd (Company No.: -202101014451 ) (“kami”).
						Untuk tujuan notis bertulis ini (“Notis”), terma-terma “data peribadi” dan “pemprosesan” mempunyai maksud
						yang sama seperti yang ditakrif dalam Akta tersebut.
					</Typography>
					<Typography style={typographyStyle}>
						This Notice serves to inform you that your personal data will be and/or have been processed by us or
						on our behalf and you hereby give your consent to the processing of your personal data.
					</Typography>
					<Typography style={typographyStyle}>
						Notis ini bertujuan untuk memaklumkan kepada anda bahawa data peribadi anda akan dan/atau telah diproses
						oleh atau bagi pihak kami dan anda dengan ini memberikan persetujuan anda bagi pemprosesan data peribadi anda
					</Typography>
				</Box>

					<Box style={sectionContainer}>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<CustomTextfield
									name={getSignature(pdpa_co_type)} formValue={getSignature(pdpa_co_type)?.signature_url_s3} label="Signature" typeOfField="signature" sigCanvas={coRef}
									saveSignature={(e) => saveSignature(e, coRef, pdpa_co_type, formValues.co_name, formValues.co_ic_passport)} savedSignature={savedSignature}
									clearSignature={(e) => clearSignature(e, coRef, pdpa_co_type)}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<CustomTextfield name='co_name' formValue={formValues.co_name} label="Full Name" typeOfField="text1" control={control} errors={errors} readOnly={true} />
								<CustomTextfield name='co_ic_passport' formValue={formValues.co_ic_passport} label="IC / Passport" typeOfField="text1" control={control} errors={errors} readOnly={true} />
								<CustomTextfield name='pdpa_co_date' formValue={getSignature(pdpa_co_type)?.date} label="Date" typeOfField="datePicker" onChangeVal={(name, val) => handleChange(pdpa_co_type, 'date', val)} disableFuture={false} disablePast={true} />
							</Grid>
						</Grid>
					</Box>
				</>

				<div style={{ marginTop: '100px' }}></div>

				<div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '30px' }}>
					<button className='t-accept-button' onClick={goback}>Previous</button>
					<button type="submit" className='t-accept-button'>Next</button>
				</div>
			</Box>
		</form>
	);
}

export default Pdpa